import { useCallback, useEffect, useRef, useState } from 'react';

import {
  autoResolveRequest,
  INBOX_MAX_PARALLEL,
  manualResolveRequest,
  pendingInboxIds,
  resolveChainKey,
  rowStatusAfterIdentify,
  validateInvoiceFile,
} from '../../../functions/ProviderInbox.Functions';
import {
  InboxRow,
  InboxRowError,
  ProviderInboxIdentifyResult,
  ProviderInboxResolveRequest,
  ProviderInboxResolveResult,
} from '../../../models/Entities/ProviderInbox/ProviderInbox';
import { ErrorType } from '../../../models/HttpError';

export interface InboxQueueCallbacks {
  identify: (file: File) => Promise<ProviderInboxIdentifyResult>;
  resolve: (inboxId: string, body: ProviderInboxResolveRequest) => Promise<ProviderInboxResolveResult>;
  discard: (inboxId: string) => Promise<void>;
  /** Una fila quedó resuelta (para refrescar la lista de proveedores). */
  onResolved?: (row: InboxRow) => void;
  /** Lecturas en paralelo (por defecto 3). */
  maxParallel?: number;
}

export interface InboxQueue {
  rows: InboxRow[];
  /** Agrega archivos: los inválidos quedan con su motivo sin llamar a la API; el resto entra en la cola. */
  addFiles: (files: File[]) => void;
  /** Vuelve a intentar la etapa que falló (lectura o alta). */
  retry: (key: string) => void;
  /** "Completar": crea el proveedor con los datos del formulario. Rechaza con ErrorType (el formulario lo muestra). */
  complete: (key: string, values: { businessName: string; taxId?: string | null }) => Promise<void>;
  /** Descarta una fila (DELETE de la bandeja si el archivo ya se subió). */
  discardRow: (key: string) => Promise<void>;
  /** Cierre: descarta lo que quedó en la bandeja sin resolver y las lecturas que terminen después; vacía la lista. */
  closeAll: () => Promise<void>;
}

const errorOf = (stage: InboxRowError['stage'], error: unknown): InboxRowError => {
  const typed = error as Partial<ErrorType> | undefined;
  return {
    stage,
    code: Number(typed?.ErrorCode ?? 0),
    message: typed?.ErrorMessage ?? (error instanceof Error ? error.message : ''),
    errors: (typed?.Errors ?? []).map(String),
  };
};

let keySeed = 0;

/**
 * Cola del alta desde factura (spec 009, US4, FR-021): como máximo 3 lecturas (`identify`) en paralelo; con
 * `UseExisting` / `CreateProvider` resuelve sola (DEC-006) y encadena las resoluciones del mismo CUIT para que dos
 * facturas del mismo emisor no compitan; "Completar" resuelve con los datos del formulario; "Reintentar" repite la etapa
 * que falló. Cerrar descarta lo que quedó sin resolver y también las lecturas que terminan con el drawer cerrado.
 */
export const useInboxQueue = (callbacks: InboxQueueCallbacks): InboxQueue => {
  const [rows, setRows] = useState<InboxRow[]>([]);
  const rowsRef = useRef<InboxRow[]>([]);
  const active = useRef(0);
  /** Sesión: cambia al cerrar; una lectura de otra sesión se descarta al terminar. */
  const session = useRef(0);
  const chains = useRef(new Map<string, Promise<unknown>>());
  const api = useRef(callbacks);
  api.current = callbacks;

  const commit = (next: InboxRow[]) => {
    rowsRef.current = next;
    setRows(next);
  };

  const update = (key: string, patch: Partial<InboxRow>) => {
    commit(rowsRef.current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const rowOf = (key: string) => rowsRef.current.find((row) => row.key === key);

  /** Resuelve una fila; las del mismo CUIT / proveedor esperan a la anterior. `manual` = desde "Completar" (rechaza). */
  const enqueueResolve = (key: string, request: ProviderInboxResolveRequest, manual: boolean): Promise<void> => {
    const row = rowOf(key);
    const inboxId = row?.identify?.inboxId;
    if (!row || !inboxId) return Promise.resolve();
    const mySession = session.current;
    update(key, { status: 'resolving', error: undefined });
    const run = async () => {
      if (mySession !== session.current) return;
      try {
        const result = await api.current.resolve(inboxId, request);
        if (mySession !== session.current) return;
        update(key, {
          status: result.providerCreated ? 'resolvedCreated' : 'resolvedExisting',
          resolved: {
            providerId: result.providerId,
            providerCreated: result.providerCreated,
            businessName: result.businessName,
            providerFileId: result.providerFile?.id ?? null,
            importUrl: result.importUrl,
          },
        });
        api.current.onResolved?.(rowOf(key)!);
      } catch (error: unknown) {
        if (mySession !== session.current) return;
        const already = (error as { AlreadyResolved?: { providerId: number; providerFileId?: string | null } })?.AlreadyResolved;
        if (already) {
          update(key, {
            status: 'resolvedExisting',
            resolved: {
              providerId: already.providerId,
              providerCreated: false,
              businessName: row.identify?.matchedProvider?.businessName ?? row.identify?.voucher?.issuer?.businessName,
              providerFileId: already.providerFileId ?? null,
              alreadyResolved: true,
            },
          });
          api.current.onResolved?.(rowOf(key)!);
          return;
        }
        // Desde "Completar" la fila vuelve a "Faltan datos" con el error a la vista y el formulario lo muestra.
        update(key, { status: manual ? 'needsInput' : 'error', error: errorOf('resolve', error) });
        if (manual) throw error;
      }
    };
    const chainKey = resolveChainKey(row.identify, request);
    if (!chainKey) return run();
    const previous = chains.current.get(chainKey) ?? Promise.resolve();
    const next = previous.catch(() => undefined).then(run);
    chains.current.set(
      chainKey,
      next.catch(() => undefined)
    );
    return next;
  };

  const startIdentify = (key: string) => {
    const row = rowOf(key);
    if (!row) return;
    const mySession = session.current;
    active.current++;
    update(key, { status: 'reading', error: undefined });
    api.current
      .identify(row.file)
      .then((result) => {
        if (mySession !== session.current) {
          // Terminó con el drawer cerrado: el archivo no queda en la bandeja.
          api.current.discard(result.inboxId).catch(() => undefined);
          return;
        }
        const status = rowStatusAfterIdentify(result);
        update(key, { identify: result, status });
        const request = autoResolveRequest(result);
        if (status === 'resolving' && request) enqueueResolve(key, request, false);
      })
      .catch((error: unknown) => {
        if (mySession !== session.current) return;
        update(key, { status: 'error', error: errorOf('identify', error) });
      })
      .finally(() => {
        active.current--;
        if (mySession === session.current) pump();
      });
  };

  const pump = () => {
    const max = api.current.maxParallel ?? INBOX_MAX_PARALLEL;
    while (active.current < max) {
      const next = rowsRef.current.find((row) => row.status === 'queued');
      if (!next) return;
      startIdentify(next.key);
    }
  };

  const addFiles = useCallback((files: File[]) => {
    const added: InboxRow[] = files.map((file) => {
      const invalidReason = validateInvoiceFile(file);
      return { key: `inbox-${++keySeed}`, file, status: invalidReason ? 'invalid' : 'queued', invalidReason };
    });
    commit([...rowsRef.current, ...added]);
    pump();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = useCallback((key: string) => {
    const row = rowOf(key);
    if (!row || row.status !== 'error') return;
    if (row.error?.stage === 'identify' || !row.identify) {
      update(key, { status: 'queued', error: undefined });
      pump();
      return;
    }
    const request = autoResolveRequest(row.identify);
    if (request) enqueueResolve(key, request, false);
    else update(key, { status: 'needsInput' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const complete = useCallback(
    (key: string, values: { businessName: string; taxId?: string | null }) => enqueueResolve(key, manualResolveRequest(values), true),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const discardRow = useCallback(async (key: string) => {
    const row = rowOf(key);
    if (!row) return;
    if (!row.identify?.inboxId) {
      commit(rowsRef.current.filter((item) => item.key !== key));
      return;
    }
    update(key, { status: 'discarded', error: undefined });
    await api.current.discard(row.identify.inboxId).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeAll = useCallback(async () => {
    const ids = pendingInboxIds(rowsRef.current);
    session.current++;
    chains.current.clear();
    commit([]);
    await Promise.all(ids.map((id) => api.current.discard(id).catch(() => undefined)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Desmontaje: lo que termine después se descarta.
  useEffect(
    () => () => {
      session.current++;
    },
    []
  );

  return { rows, addFiles, retry, complete, discardRow, closeAll };
};

export default useInboxQueue;
